import React from 'react';
import ReactDOM from 'react-dom';
import { useState, useMemo } from 'react';
import { toast, Toaster } from 'react-hot-toast';
import { format } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import apiClient from './api/client';
import Select from 'react-select';
import { useCompanies, useTicketStatuses, useTicketPriorities, useTicketSubStatuses, useAdminUsers } from './hooks';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';

import ViewTicketHistoryModal from './components/ViewTicketHistoryModal';
import UserDetailsModal from './components/UserDetailsModal';
import AssignResourceModal from './components/AssignResourceModal';
import EditTicketModal from './components/EditTicketModal';
import NotesModal from './components/NotesModal';
import SpecificationDocumentModal from './components/SpecificationDocumentModal';
import ThreadTab from './components/ThreadTab';
import NotesTab from './components/NotesTab';
import DocumentsTab from './components/DocumentsTab';
import DeveloperDocsTab from './components/DeveloperDocsTab';
import { useCurrentDomain } from "./context/DomainContext";
import { useMultiSelect, useDatePreset } from './TicketFilterUtils';

const BRAND_GREEN = '#0aa375';
const allowedDomainsForManageTicket = ['greenyourbills.com', 'ab.greenyourbills.com'];

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '30px',
    height: '30px',
    fontSize: '12px',
    borderColor: '#dee2e6',
    boxShadow: 'none',
    '&:hover': { borderColor: BRAND_GREEN },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, padding: '0 2px' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 4px' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
  option: (base, state) => ({
    ...base,
    fontSize: '12px',
    backgroundColor: state.isSelected ? BRAND_GREEN : state.isFocused ? '#e6f7f2' : 'white',
    color: state.isSelected ? 'white' : '#333',
  }),
  multiValue: (base) => ({ ...base, fontSize: '11px' }),
  singleValue: (base) => ({ ...base, fontSize: '12px' }),
  placeholder: (base) => ({ ...base, fontSize: '12px', color: '#adb5bd' }),
};

const labelStyle = {
  fontSize: '10px', fontWeight: 600, color: '#212529',
  textTransform: 'uppercase', letterSpacing: '0.3px',
  marginBottom: '3px', display: 'block'
};

const inputStyle = {
  height: '30px', fontSize: '12px', padding: '2px 8px',
  border: '1px solid #dee2e6', borderRadius: '4px', width: '100%',
};

const pageSizeOptions = [10, 25, 50, 100].map((n) => ({ value: n, label: `${n} / page` }));

export default function ManageTicket() {
  const { domain } = useCurrentDomain();
  const { companies, loading: companiesLoading } = useCompanies(domain);
  const { statuses } = useTicketStatuses(domain);
  const { priorities } = useTicketPriorities(domain);
  const { subStatuses } = useTicketSubStatuses(domain);
  const { assignTos, loading: usersLoading } = useAdminUsers();

  const hasManageTicketAccess = allowedDomainsForManageTicket.includes(domain?.toLowerCase() || '');

  const accountOptions = useMemo(() => [
    ...(companies.length > 0 ? [{ value: 'all', label: 'Select All' }] : []),
    ...companies.map(c => ({ value: c.value, label: c.label })),
  ], [companies]);

  const statusOptions = useMemo(() => {
    const items = Array.isArray(statuses) ? statuses.map(s => ({ value: s.value, label: s.label })) : [];
    return [...(items.length > 0 ? [{ value: 'all', label: 'Select All' }] : []), ...items];
  }, [statuses]);

  const priorityOptions = useMemo(() => {
    const items = Array.isArray(priorities) ? priorities.map(p => ({ value: p.value, label: p.label })) : [];
    return [...(items.length > 0 ? [{ value: 'all', label: 'Select All' }] : []), ...items];
  }, [priorities]);

  const subStatusOptions = useMemo(() => {
    const items = Array.isArray(subStatuses) ? subStatuses.map(ss => ({ value: ss.value, label: ss.label })) : [];
    return [...(items.length > 0 ? [{ value: 'all', label: 'Select All' }] : []), ...items];
  }, [subStatuses]);

  const assignToOptions = useMemo(() => {
    const items = Array.isArray(assignTos) ? assignTos.map(u => ({ value: u.value, label: u.label })) : [];
    return [...(items.length > 0 ? [{ value: 'all', label: 'Select All' }] : []), ...items];
  }, [assignTos]);

  const accounts = useMultiSelect(accountOptions, 'accounts');
  const statusSel = useMultiSelect(statusOptions, 'statuses', true);
  const priorities_ = useMultiSelect(priorityOptions, 'priorities');
  const subStatSel = useMultiSelect(subStatusOptions, 'subStatuses');
  const assignSel = useMultiSelect(assignToOptions, 'assignTos');
  const dateFilter = useDatePreset();

  const [ticketNum, setTicketNum] = useState('');
  const [subject, setSubject] = useState('');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [tickets, setTickets] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [pendingReminders, setPendingReminders] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [activeTabs, setActiveTabs] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const handleSearch = async (pageOverride, pageSizeOverride) => {
    if (!domain) { toast.error('Please select a domain first'); return; }
    const currentPage = pageOverride ?? page;
    const currentPageSize = pageSizeOverride ?? pageSize;
    setSearchLoading(true); setError(null);
    try {
      const payload = {
        domain,
        companyId: accounts.values,
        ticketNumber: ticketNum.trim() || undefined,
        subject: subject.trim() || undefined,
        status: statusSel.values,
        priority: priorities_.values,
        subStatus: subStatSel.values,
        assignedTo: assignSel.values,
        fromDate: dateFilter.fromDate || undefined,
        toDate: dateFilter.toDate || undefined,
        estimatedDelivery: estimatedDelivery || undefined,
        page: currentPage,
        pageSize: currentPageSize,
      };
      const res = await apiClient.post(`/api/tickets/search`, payload);
      setTickets(res.data.items || []);
      setTotalCount(res.data.totalCount || 0);
      setPage(currentPage);
      setPageSize(currentPageSize);
      if (!res.data.items?.length) toast('No tickets found');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to search tickets';
      setError(msg); toast.error(msg);
    } finally {
      setSearchLoading(false);
    }
  };

  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page || searchLoading) return;
    handleSearch(newPage);
  };

  const handlePageSizeChange = (opt) => {
    handleSearch(1, opt.value);
  };

  const handleReset = () => {
    accounts.reset(); statusSel.reset(); priorities_.reset();
    subStatSel.reset(); assignSel.reset(); dateFilter.reset();
    setTicketNum(''); setSubject(''); setEstimatedDelivery('');
    setTickets([]); setTotalCount(0); setError(null);
    setPage(1); setPageSize(25);
  };

  const handleColumnClick = (action, ticket) => {
    console.log("162", ticket)
    setSelectedTicket(ticket);
    switch (action) {
      case 'view': setShowViewModal(true); break;
      case 'user': setShowUserModal(true); break;
      case 'assign': setShowAssignModal(true); break;
      case 'edit': setShowEditModal(true); break;
      case 'notes': setShowNotesModal(true); break;
      case 'doc': setShowDocModal(true); break;
      default: toast(`[${action.toUpperCase()}] Ticket #${ticket?.szTicketNumber || '—'}`);
    }
  };

  const handleToggleReminder = async (ticket, shouldAdd) => {
    const ticketId = ticket.bgTicketId;
    if (!ticketId || !domain) { toast.error("Cannot update reminder – missing data"); return; }
    setPendingReminders(prev => ({ ...prev, [ticketId]: shouldAdd }));
    try {
      const payload = { domain };
      if (shouldAdd) {
        await apiClient.post(`/api/tickets/${ticketId}/reminder`, payload);
        toast.success("Reminder added");
      } else {
        await apiClient.delete(`/api/tickets/${ticketId}/reminder`, { data: payload });
        toast.success("Reminder removed");
      }
      setPendingReminders(prev => { const c = { ...prev }; delete c[ticketId]; return c; });
      setTickets(prev => prev.map(t => t.bgTicketId === ticketId ? { ...t, iReminderId: shouldAdd ? 1 : 0 } : t));
    } catch (err) {
      setPendingReminders(prev => { const c = { ...prev }; delete c[ticketId]; return c; });
      toast.error(err.response?.data?.message || "Failed to update reminder");
    }
  };

  return (
    <>
      <Toaster position="top-right" />
      <style>{`
        .tbl-ticket td, .tbl-ticket th { padding: 4px 8px !important; vertical-align: middle; }
        .tbl-ticket thead th { background: #f8f9fa; font-size: 10.5px; font-weight: 600; color: #495057; text-transform: uppercase; letter-spacing: 0.3px; border-bottom: 1px solid #dee2e6; white-space: nowrap; }
        .tbl-ticket tbody tr { height: 38px; }
        .tbl-ticket tbody tr:hover { background-color: #f0fdf8; }
        .tbl-ticket .lnk { color: ${BRAND_GREEN} !important; font-size: 12px; background: none; border: none; padding: 0; cursor: pointer; text-decoration: none; }
        .tbl-ticket .lnk:hover { text-decoration: underline; }
        .tbl-ticket .lnk-red { color: #dc3545 !important; font-size: 12px; background: none; border: none; padding: 0; cursor: pointer; }
        .tbl-ticket .lnk-red:hover { text-decoration: underline; }
        .form-control-xs { height: 30px !important; font-size: 12px !important; padding: 2px 8px !important; }
        .react-datepicker-popper { z-index: 1055 !important; }
        .sep { color: #ced4da; margin: 0 2px; font-size: 10px; }
      `}</style>

      <div className="bg-white" style={{ height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' }}>
        <div className="card shadow-sm border-0" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* Filter Bar */}
          <div className="card-body py-2 px-3" style={{ flex: '0 0 auto' }}>
            <div className="row g-2 align-items-end">

              <div className="col-lg-6 col-md-4">
                <label style={labelStyle}>Account</label>
                <Select {...accounts.props('Select account...')} isDisabled={companiesLoading}
                  styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="react-select" />
              </div>

              <div className="col-lg-1 col-md-2">
                <label style={labelStyle}>Ticket #</label>
                <input type="text" className="form-control form-control-xs" value={ticketNum}
                  onChange={(e) => setTicketNum(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                  placeholder="BS-XXXX" />
              </div>

              <div className="col-lg-3 col-md-4">
                <label style={labelStyle}>Subject</label>
                <input type="text" className="form-control form-control-xs" value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                  placeholder="Search subject..." />
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>Status</label>
                <Select {...statusSel.props('Select status...')}
                  styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="react-select" />
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>Sub Status</label>
                <Select {...subStatSel.props('Select sub status...')}
                  styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="react-select" />
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>Priority</label>
                <Select {...priorities_.props('Select priority...')}
                  styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="react-select" />
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>Assign To</label>
                <Select {...assignSel.props('Select assignee...')}
                  styles={selectStyles} menuPortalTarget={document.body} classNamePrefix="react-select" />
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>Ticket Created</label>
                <select className="form-control form-control-xs"
                  value={dateFilter.preset} onChange={(e) => dateFilter.applyPreset(e.target.value)}>
                  <option value="0">All</option>
                  <option value="1">Today</option>
                  <option value="2">This Week</option>
                  <option value="3">This Month</option>
                  <option value="4">This Quarter</option>
                  <option value="5">This Year</option>
                  <option value="6">Last Week</option>
                  <option value="7">Last Month</option>
                  <option value="9">Last Quarter</option>
                  <option value="8">Last Year</option>
                </select>
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>From / To Date</label>
                <DatePicker
                  selectsRange
                  startDate={dateFilter.fromDate ? new Date(dateFilter.fromDate) : null}
                  endDate={dateFilter.toDate ? new Date(dateFilter.toDate) : null}
                  onChange={([start, end]) => dateFilter.setRange(start, end)}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY ~ MM/DD/YYYY"
                  className="form-control form-control-xs"
                  wrapperClassName="w-100"
                  isClearable showMonthDropdown showYearDropdown dropdownMode="select"
                  monthsShown={2}
                  popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>

              <div className="col-lg-2 col-md-3">
                <label style={labelStyle}>Est. Delivery</label>
                <DatePicker
                  selected={estimatedDelivery ? new Date(estimatedDelivery) : null}
                  onChange={(date) => setEstimatedDelivery(date ? format(date, 'yyyy-MM-dd') : '')}
                  dateFormat="MM/dd/yyyy" placeholderText="MM/DD/YYYY"
                  className="form-control form-control-xs" wrapperClassName="w-100"
                  isClearable showMonthDropdown showYearDropdown dropdownMode="select"
                  popperContainer={({ children }) => ReactDOM.createPortal(children, document.body)}
                  popperProps={{ strategy: 'fixed' }}
                />
              </div>

            </div>

            <div className="d-flex justify-content-center gap-2 mt-2">
              <button
                className="btn btn-outline-secondary btn-sm px-3"
                style={{ fontSize: '12px', height: '28px', padding: '2px 14px' }}
                onClick={handleReset}
                disabled={searchLoading || !hasManageTicketAccess}
              >
                Reset
              </button>
              <button
                className="btn btn-sm text-white px-3 fw-semibold"
                style={{ backgroundColor: (searchLoading || !hasManageTicketAccess) ? '#6c757d' : BRAND_GREEN, border: 'none', fontSize: '12px', height: '28px', padding: '2px 16px' }}
                onClick={() => handleSearch(1)}
                disabled={searchLoading || !hasManageTicketAccess}
              >
                {searchLoading
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Searching...</>
                  : 'Search'}
              </button>
            </div>

            {!hasManageTicketAccess && domain && (
              <div className="alert alert-danger mt-2 mb-0 py-2 text-center" style={{ fontSize: '12px' }}>
                <i className="fas fa-exclamation-triangle me-1"></i>
                The selected domain doesn't have Manage Ticket feature.
              </div>
            )}
            {error && <div className="alert alert-danger mt-2 mb-0 py-2" style={{ fontSize: '12px' }}>{error}</div>}
          </div>

          <hr className="my-0" />

          {hasManageTicketAccess && (
            <div className="card-body p-0" style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="px-3 py-1 border-bottom bg-light d-flex align-items-center" style={{ flex: '0 0 auto' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>
                  Ticket records ({totalCount})
                </span>
              </div>

              {searchLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm" style={{ color: BRAND_GREEN }} role="status" />
                </div>
              ) : tickets.length === 0 ? (
                <div className="text-center py-4 text-muted" style={{ fontSize: '13px' }}>No tickets found.</div>
              ) : (
                <>
                  <div className="table-responsive" style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
                    <table className="table table-hover mb-0 tbl-ticket" style={{ width: '100%' }}>
                      <thead className="sticky-top">
                        <tr>
                          <th style={{ width: '28px' }}></th>
                          <th style={{ width: '70px' }}>#</th>
                          <th>Subject</th>
                          <th>User</th>
                          <th style={{ width: '100px' }}>Created On</th>
                          <th>Account</th>
                          <th style={{ width: '70px' }}>Priority</th>
                          <th style={{ width: '70px' }}>Status</th>
                          <th style={{ width: '110px' }}>Sub Status</th>
                          <th style={{ width: '60px' }}>Last Activity</th>
                          <th style={{ width: '80px' }}>Assigned To</th>
                          <th style={{ width: '40px' }} className="text-center">Edit</th>
                          <th style={{ width: '65px' }} className="text-center">Notes</th>
                          <th style={{ width: '60px' }} className="text-center">Reminder</th>
                          <th style={{ width: '68px' }}>Est.Delv</th>
                          <th style={{ width: '48px' }} className="text-center">Doc</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tickets.map((ticket) => {
                          const ticketId = ticket.bgTicketId;
                          const isExpanded = expandedRows.has(ticketId);
                          const currentTab = activeTabs[ticketId] ?? 0;

                          return (
                            <React.Fragment key={ticketId}>
                              <tr>
                                <td className="text-center">
                                  <button className="p-0 border-0 bg-transparent" style={{ width: '20px', height: '20px' }}
                                    onClick={() => {
                                      const newExpanded = new Set(expandedRows);
                                      if (isExpanded) newExpanded.delete(ticketId); else newExpanded.add(ticketId);
                                      setExpandedRows(newExpanded);
                                      if (!isExpanded) setActiveTabs(prev => ({ ...prev, [ticketId]: 0 }));
                                    }}>
                                    {isExpanded
                                      ? <ChevronUpIcon style={{ width: '14px', height: '14px', color: '#6c757d' }} />
                                      : <ChevronDownIcon style={{ width: '14px', height: '14px', color: '#6c757d' }} />}
                                  </button>
                                </td>

                                <td>
                                  <button className="lnk" onClick={() => handleColumnClick('view', ticket)}>
                                    {ticket.szTicketNumber || '—'}
                                  </button>
                                </td>

                                <td style={{ fontSize: '12px', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>
                                  {ticket.szSubject || '—'}
                                </td>

                                <td>
                                  <button className="lnk" onClick={() => handleColumnClick('user', ticket)}>
                                    {ticket.szRaisedBy || '—'}
                                  </button>
                                </td>

                                <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  {ticket.dtRaisedOn ? format(new Date(ticket.dtRaisedOn), 'dd-MMM-yy hh:mm a') : '—'}
                                </td>

                                <td style={{ fontSize: '12px', whiteSpace: 'normal', lineHeight: '1.3' }}>
                                  {ticket.szCompanyName || '—'}
                                </td>

                                <td style={{ fontSize: '12px' }}>{ticket.szPriority || '—'}</td>
                                <td style={{ fontSize: '12px' }}>{ticket.szStatus || '—'}</td>
                                <td style={{ fontSize: '12px' }}>{ticket.subStatus || '—'}</td>

                                <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  {ticket.dtLastActivity ? format(new Date(ticket.dtLastActivity), 'dd-MMM-yy hh:mm a') : '—'}
                                </td>

                                <td>
                                  <button className="lnk" onClick={() => handleColumnClick('assign', ticket)}>
                                    {ticket.szAssignedTo || 'Assign'}
                                  </button>
                                </td>

                                <td className="text-center">
                                  <button className="lnk" onClick={() => handleColumnClick('edit', ticket)}>Edit</button>
                                </td>

                                <td className="text-center">
                                  <button className="lnk" onClick={() => handleColumnClick('notes', ticket)}>
                                    Notes({ticket.count || 0})
                                  </button>
                                </td>

                                <td className="text-center">
                                  {(() => {
                                    const isPending = ticketId in pendingReminders;
                                    const hasReminder = isPending ? pendingReminders[ticketId] : (ticket.iReminderId > 0);
                                    return isPending
                                      ? <span className="spinner-border spinner-border-sm text-muted" style={{ width: '12px', height: '12px' }} />
                                      : hasReminder
                                        ? <button className="lnk-red" onClick={() => handleToggleReminder(ticket, false)}>Remove</button>
                                        : <button className="lnk" onClick={() => handleToggleReminder(ticket, true)}>Add</button>;
                                  })()}
                                </td>

                                <td style={{ fontSize: '11px', whiteSpace: 'nowrap' }}>
                                  {ticket.dtEstimatedDelivery ? format(new Date(ticket.dtEstimatedDelivery), 'dd-MMM-yy') : '—'}
                                </td>

                                <td className="text-center">
                                  <button className="lnk" onClick={() => handleColumnClick('doc', ticket)}>Upload</button>
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr>
                                  <td colSpan={16} className="p-0">
                                    <div style={{ borderTop: '1px solid #dee2e6', background: '#fafafa' }}>
                                      <div className="d-flex gap-1 bg-white border-bottom px-3 pt-2">
                                        {['Thread', 'Notes', 'Documents', 'Developer Docs'].map((tabName, idx) => (
                                          <button key={idx}
                                            onClick={() => setActiveTabs(prev => ({ ...prev, [ticketId]: idx }))}
                                            style={{
                                              fontSize: '11px', fontWeight: 600, padding: '4px 14px',
                                              borderRadius: '4px 4px 0 0', border: '1px solid',
                                              marginBottom: '-1px', cursor: 'pointer',
                                              backgroundColor: currentTab === idx ? BRAND_GREEN : '#fff',
                                              borderColor: currentTab === idx ? BRAND_GREEN : '#dee2e6',
                                              color: currentTab === idx ? 'white' : '#495057',
                                            }}>
                                            {tabName}
                                          </button>
                                        ))}
                                      </div>
                                      <div className="bg-white p-3" style={{ borderBottom: '1px solid #dee2e6' }}>
                                        {currentTab === 0 && <ThreadTab ticketId={ticketId} domain={domain} />}
                                        {currentTab === 1 && <NotesTab ticketId={ticketId} domain={domain} />}
                                        {currentTab === 2 && <DocumentsTab ticketId={ticketId} domain={domain} />}
                                        {currentTab === 3 && <DeveloperDocsTab ticketId={ticketId} domain={domain} />}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div style={{
                    flex: '0 0 auto',
                    padding: '10px 20px', borderTop: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#fafafa', flexWrap: 'wrap', gap: '8px',
                  }}>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                      Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                    </span>

                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1 || searchLoading}
                        style={{
                          padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                          background: page === 1 ? '#f3f4f6' : '#fff',
                          color: page === 1 ? '#9ca3af' : BRAND_GREEN,
                          cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                        }}
                      >‹ Prev</button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => {
                          if (totalPages <= 7) return true;
                          if (p === 1 || p === totalPages) return true;
                          if (p >= page - 2 && p <= page + 2) return true;
                          return false;
                        })
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, idx) =>
                          p === '...' ? (
                            <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                          ) : (
                            <button
                              key={p}
                              onClick={() => goToPage(p)}
                              disabled={searchLoading}
                              style={{
                                width: 28, height: 28, borderRadius: 6, border: '1px solid #d1fae5',
                                background: page === p ? BRAND_GREEN : '#fff',
                                color: page === p ? '#fff' : BRAND_GREEN,
                                fontWeight: page === p ? 700 : 400,
                                cursor: 'pointer', fontSize: 12
                              }}
                            >{p}</button>
                          )
                        )
                      }

                      <button
                        onClick={() => goToPage(page + 1)}
                        disabled={page === totalPages || searchLoading}
                        style={{
                          padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                          background: page === totalPages ? '#f3f4f6' : '#fff',
                          color: page === totalPages ? '#9ca3af' : BRAND_GREEN,
                          cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                        }}
                      >Next ›</button>
                    </div>

                    <div style={{ width: '110px' }}>
                      <Select
                        options={pageSizeOptions}
                        value={pageSizeOptions.find(o => o.value === pageSize)}
                        onChange={handlePageSizeChange}
                        isSearchable={false}
                        isDisabled={searchLoading}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showViewModal && selectedTicket && (
        <ViewTicketHistoryModal ticket={selectedTicket} domain={domain}
          onClose={() => { setShowViewModal(false); setSelectedTicket(null); }} />
      )}
      {showUserModal && selectedTicket && (
        <UserDetailsModal user={{
          szUserId: selectedTicket.sZ_USER_ID,
          szCompanyId: selectedTicket.szCompanyId, szUserName: selectedTicket.szRaisedBy,
          szCompanyName: selectedTicket.szCompanyName, szDomainName: domain
        }} domain={domain}
          onClose={() => { setShowUserModal(false); setSelectedTicket(null); }} />
      )}
      {showAssignModal && selectedTicket && (
        <AssignResourceModal ticket={selectedTicket} domain={domain}
          onClose={() => { setShowAssignModal(false); setSelectedTicket(null); }} />
      )}
      {showEditModal && selectedTicket && (
        <EditTicketModal ticket={selectedTicket} domain={domain}
          onClose={() => { setShowEditModal(false); setSelectedTicket(null); }} />
      )}
      {showNotesModal && selectedTicket && (
        <NotesModal ticket={selectedTicket} domain={domain}
          onClose={() => { setShowNotesModal(false); setSelectedTicket(null); }} />
      )}
      {showDocModal && selectedTicket && (
        <SpecificationDocumentModal ticket={selectedTicket} domain={domain}
          onClose={() => { setShowDocModal(false); setSelectedTicket(null); }} />
      )}
    </>
  );
}